
import { supabase } from '@/lib/supabase';

export interface CTraderConnection {
  id: string;
  api_name: string;
  client_id: string;
  status: string;
  expire_date: string | null;
  last_refresh_attempt: string | null;
  refresh_status: string | null;
  refresh_error_message: string | null;
  last_balance_sync: string | null;
  balance_sync_status: string | null;
  balance_sync_error: string | null;
  created_at: string;
  csp_account_name?: string;
}

export interface RefreshResult {
  connectionId: string;
  apiName: string;
  success: boolean;
  error: string | null;
}

export interface BalanceSyncResult {
  connectionId: string;
  apiName: string;
  success: boolean;
  error: string | null;
  balanceUpdates: Array<{
    accountId: string;
    previousBalance: number;
    newBalance: number;
    change: number;
  }>;
}

export interface RefreshSummary {
  total: number;
  successful: number;
  failed: number;
}

export interface RefreshResponse {
  success: boolean;
  message: string;
  results: RefreshResult[];
  summary: RefreshSummary;
}

export interface BalanceSyncResponse {
  success: boolean;
  message: string;
  results: BalanceSyncResult[];
  summary: RefreshSummary;
}

export const ctraderTokenRefreshService = {
  async getAllCTraderConnections(): Promise<CTraderConnection[]> {
    const { data, error } = await supabase
      .from('api_connections')
      .select(`
        id,
        api_name,
        client_id,
        status,
        expire_date,
        last_refresh_attempt,
        refresh_status,
        refresh_error_message,
        last_balance_sync,
        balance_sync_status,
        balance_sync_error,
        created_at,
        csp_accounts!inner(csp_account_name)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(conn => ({
      ...conn,
      csp_account_name: (conn.csp_accounts as any)?.csp_account_name
    }));
  },

  async refreshAllTokens(): Promise<RefreshResponse> {
    const { data, error } = await supabase.functions.invoke('refresh-ctrader-tokens', {
      body: { refreshAll: true }
    });

    if (error) throw error;
    return data;
  },

  async refreshSingleToken(connectionId: string): Promise<RefreshResponse> {
    const { data, error } = await supabase.functions.invoke('refresh-ctrader-tokens', {
      body: { 
        connectionIds: [connectionId],
        refreshAll: false 
      }
    });

    if (error) throw error;
    return data;
  },

  async refreshMultipleTokens(connectionIds: string[]): Promise<RefreshResponse> {
    const { data, error } = await supabase.functions.invoke('refresh-ctrader-tokens', {
      body: { 
        connectionIds,
        refreshAll: false 
      }
    });

    if (error) throw error;
    return data;
  },

  async syncAccountBalances(): Promise<BalanceSyncResponse> {
    const { data, error } = await supabase.functions.invoke('sync-ctrader-accounts', {
      body: { syncAll: true }
    });

    if (error) throw error;
    return data;
  },

  async syncSingleAccountBalance(connectionId: string): Promise<BalanceSyncResponse> {
    const { data, error } = await supabase.functions.invoke('sync-ctrader-accounts', {
      body: { 
        connectionIds: [connectionId],
        syncAll: false 
      }
    });

    if (error) throw error;
    return data;
  },

  async syncMultipleAccountBalances(connectionIds: string[]): Promise<BalanceSyncResponse> {
    const { data, error } = await supabase.functions.invoke('sync-ctrader-accounts', {
      body: { 
        connectionIds,
        syncAll: false 
      }
    });

    if (error) throw error;
    return data;
  }
};
